import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useLang } from "../lib/i18n";
import { PageHeader, Options } from "../components/common";
import { STAGES } from "../lib/constants";
import { fullName, fmtDate, srcLabel } from "../lib/helpers";
import { useAuth } from "../lib/auth";
import { useTeam } from "../components/leads/leadUtils";

function Filters({ list, f, setF, team, t, lang }) {
  const sources = [...new Set(list.map((c) => c.lead_source).filter(Boolean))].sort();
  return (
    <div className="flex flex-wrap gap-2 mb-3" data-testid="pipeline-filters">
      <select className="inp h-10 text-sm w-auto" value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} data-testid="pipeline-filter-source">
        <option value="">{t("All sources", "Todas las fuentes")}</option>
        {sources.map((s) => <option key={s} value={s}>{srcLabel(s, lang)}</option>)}
      </select>
      <select className="inp h-10 text-sm w-auto" value={f.assigned} onChange={(e) => setF({ ...f, assigned: e.target.value })} data-testid="pipeline-filter-assigned">
        <option value="">{t("Everyone", "Todos")}</option>
        <option value="me">{t("Assigned to me", "Asignados a mí")}</option>
        <option value="none">{t("Unassigned", "Sin asignar")}</option>
        {team.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
      </select>
    </div>
  );
}

export default function Pipeline() {
  const { t, lang } = useLang();
  const [list, setList] = useState([]);
  const [f, setF] = useState({ source: "", assigned: "" });
  const { user } = useAuth();
  const team = useTeam();
  const names = Object.fromEntries(team.map((m) => [m.id, m.name]));
  const want = f.assigned === "me" ? user.id : f.assigned;
  const shown = list.filter((c) => (!f.source || c.lead_source === f.source) && (!f.assigned || (f.assigned === "none" ? !c.assigned_to : c.assigned_to === want)));
  const load = () => api.get("/customers").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const move = async (c, stage) => {
    try {
      await api.patch(`/customers/${c.id}/stage`, { stage });
      setList(list.map((x) => (x.id === c.id ? { ...x, stage } : x)));
      toast.success(t("Moved", "Movido"));
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <PageHeader title={t("Customer Pipeline", "Embudo de clientes")} subtitle={t("Change the stage with the selector on each card.", "Cambie la etapa con el selector de cada tarjeta.")} />
      <Filters list={list} f={f} setF={setF} team={team} t={t} lang={lang} />
      <div className="flex gap-3 overflow-x-auto pb-4 -mx-4 px-4 snap-x" data-testid="pipeline-board">
        {STAGES.map(([k, en, es]) => {
          const items = shown.filter((c) => c.stage === k);
          return (
            <div key={k} className="snap-start shrink-0 w-[280px] bg-slate-100 rounded-2xl p-2" data-testid={`pipeline-col-${k}`}>
              <div className="flex justify-between items-center px-2 py-2">
                <span className="font-bold text-sm">{lang === "es" ? es : en}</span>
                <span className="mono text-xs bg-white rounded-full px-2">{items.length}</span>
              </div>
              <div className="grid gap-2 max-h-[65vh] overflow-y-auto">
                {items.map((c) => (
                  <div key={c.id} className="bg-white rounded-xl border border-slate-200 p-3" data-testid={`pipeline-card-${c.id}`}>
                    <Link to={`/customers/${c.id}`} className="font-semibold block truncate hover:text-[#D9531E]">{fullName(c)}</Link>
                    <div className="text-xs text-slate-500 truncate">{c.service_requested || c.phone}</div>
                    <div className="flex flex-wrap gap-1 my-2 text-[11px]">
                      {c.lead_source && <span className="px-1.5 py-0.5 rounded bg-slate-100 font-semibold" data-testid={`pipeline-source-${c.id}`}>{srcLabel(c.lead_source, lang)}</span>}
                      {c.assigned_to && <span className="px-1.5 py-0.5 rounded bg-orange-50 text-[#D9531E] font-semibold" data-testid={`pipeline-assigned-${c.id}`}>{names[c.assigned_to] || "—"}</span>}
                      <span className="text-slate-400">{fmtDate((c.lead_received_at || c.created_at || "").slice(0, 10), lang)}</span>
                    </div>
                    <select className="inp h-10 text-sm" value={c.stage} onChange={(e) => move(c, e.target.value)} data-testid={`pipeline-move-${c.id}`}>
                      <Options items={STAGES} lang={lang} />
                    </select>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
